import React, { useEffect, useState } from 'react';

import moment from 'moment';

import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';
import { SkeletonHeadingItem, SkeletonItem } from '@atlaskit/menu';

import { Text } from '@qality/common-components/src/atoms/Text';
import { NoPermissionToViewIssuesBanner } from '@qality/common-components/src/molecules/NoPermissionToViewIssuesBanner';
import { BugsSummary, Issue } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';
import { StatusUtils } from '@qality/common-utils';

import BugsWithStatusList from '../../test-cycle/BugsWithStatusList';
import ClosedPercent from './ClosedPercent';
import NewIssues from './NewIssues';
import OpenPercent from './OpenPercent';
import {
  BugsContainerStyled,
  BugsListBannerStyled,
  ComponentContainerStyled,
  DotSymbolStyled,
  EmptyComponentContainerStyled,
  IconContainerStyled,
  StatusesWrapperStyled,
  SummaryWrapperStyled,
} from './ReportColumn.styled';

type Props = {
  ids: Array<number>;
};

export default function ReportBugsColumn({ ids }: Props): JSX.Element {
  const [bugsData, setBugsData] = useState<Array<Issue>>([]);
  const [bugsSummary, setBugsSummary] = useState<BugsSummary>({});
  const [expanded, setExpanded] = useState(false);
  const [missingIssues, setMissingIssues] = useState(0);
  const [loadingCompleted, setLoadingCompleted] = useState(false);

  const fetchBugsData = async () => {
    const fields = ['key', 'summary', 'issuetype', 'status', 'created'];
    let issues: Array<Issue> = await StorageOfUniqueServices.get(
      'JQLSearchService'
    ).bulkFetchIssuesByIds({
      ids,
      maxResults: 50,
      fields,
    });

    const bugsSummaryTemp: BugsSummary = {
      resolved: 0,
      pending: 0,
      newBugs: 0,
      resolvedPercent: 0,
      pendingPercent: 0,
    };

    const now = moment(Date.now());
    const fourteenDaysBefore = moment(now).subtract(14, 'days');

    issues.forEach((bug) => {
      if (StatusUtils.isDone(bug.fields.status)) {
        bugsSummaryTemp.resolved = bugsSummaryTemp.resolved + 1;
      } else {
        bugsSummaryTemp.pending = bugsSummaryTemp.pending + 1;
      }

      const bugCreatedDate = moment(bug.fields.created);
      if (bugCreatedDate.isBetween(fourteenDaysBefore, now, 'days', '[]')) {
        bugsSummaryTemp.newBugs = bugsSummaryTemp.newBugs + 1;
      }
    });

    issues = issues.sort((bug1, bug2) => {
      const isDone1 = StatusUtils.isDone(bug1.fields.status) ? 1 : 0;
      const isDone2 = StatusUtils.isDone(bug2.fields.status) ? 1 : 0;

      return isDone1 - isDone2;
    });

    bugsSummaryTemp.resolvedPercent = getPercentage(
      bugsSummaryTemp.resolved,
      bugsSummaryTemp.resolved + bugsSummaryTemp.pending
    );

    bugsSummaryTemp.pendingPercent = 100 - bugsSummaryTemp.resolvedPercent;

    const missingIssueTemp = ids.length - issues.length;

    setMissingIssues(missingIssueTemp);
    setBugsData(issues);
    setBugsSummary(bugsSummaryTemp);
    setLoadingCompleted(true);
  };

  useEffect(() => {
    if (ids.length > 0) {
      fetchBugsData();
    } else {
      setLoadingCompleted(true);
    }
  }, []);

  const getPercentage = (partialValue: number, totalValue: number): number => {
    const percent = Math.round((100 * partialValue) / totalValue);
    return Number.isInteger(percent) ? percent : 0;
  };

  if (!loadingCompleted) {
    return (
      <ComponentContainerStyled>
        <SkeletonHeadingItem />
        <SkeletonItem />
      </ComponentContainerStyled>
    );
  }

  if (ids.length > 0) {
    const bugsAmountText = ids.length > 1 ? 'bugs' : 'bug';
    return (
      <ComponentContainerStyled>
        <SummaryWrapperStyled>
          <p>
            {ids.length} {bugsAmountText} found
          </p>
          <IconContainerStyled onClick={() => setExpanded(!expanded)}>
            {expanded ? (
              <ChevronUpIcon
                label='Open'
                size='large'
                primaryColor='var(--ds-icon, var(--ds-fb-icon))'
              />
            ) : (
              <ChevronDownIcon
                label='closed'
                size='large'
                primaryColor='var(--ds-icon, , var(--ds-fb-icon))'
              />
            )}
          </IconContainerStyled>
        </SummaryWrapperStyled>
        <StatusesWrapperStyled>
          <ClosedPercent bugsSummary={bugsSummary} />
          <DotSymbolStyled />
          <OpenPercent bugsSummary={bugsSummary} />
          <DotSymbolStyled />
          <NewIssues bugsSummary={bugsSummary} />
        </StatusesWrapperStyled>
        {expanded && (
          <BugsContainerStyled>
            {bugsData.map((bug) => {
              return (
                <BugsWithStatusList
                  key={bug.id}
                  bug={bug}
                  textStyle={{
                    display: '-webkit-box',
                    '-webkit-line-clamp': '1',
                    '-webkit-box-orient': 'vertical',
                    overflow: 'hidden',
                  }}
                  justifyContent='flex-start'
                />
              );
            })}
            {missingIssues > 0 && (
              <BugsListBannerStyled>
                <NoPermissionToViewIssuesBanner
                  numberOfIssues={missingIssues}
                />
              </BugsListBannerStyled>
            )}
          </BugsContainerStyled>
        )}
      </ComponentContainerStyled>
    );
  } else {
    return (
      <EmptyComponentContainerStyled>
        <Text noData>No bugs found</Text>
      </EmptyComponentContainerStyled>
    );
  }
}
